import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import classes from "./SubscriptionPage.module.css";
import { motion } from "framer-motion";
import { FaCheck } from "react-icons/fa6";
import LinedSection from "../component/LinedSection";
import Footer from "../component/Footer";
import { getPacksApi, subApi, getSubStatusApi } from "../http/http";
import { countryCode, isValidUrl } from "../utils/constants";
import { setUser } from "../store/slices/userSlice";
import SmallLoader from "../component/SmallLoader";

const SubscriptionPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { userDetails } = useSelector((state) => state?.persisted?.userSlice);
  const { selectedLanguage } = Number(
    useSelector((state) => state?.persisted?.languageSlice?.language ?? 2),
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedPlan, setSelectedPlan] = useState(1);
  const [flow, setFlow] = useState(""); // State to track flow
  const [flowMsg, setFlowMsg] = useState({ number: "0", error: true });
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [msisdn, setMsisdn] = useState(
    searchParams.get("mn") ? searchParams.get("mn") : "",
  );

  useEffect(() => {
    if (userDetails?.token) {
      navigate("/home");
    } else {
      setPageLoading(true);
      getPacksApi(searchParams.get("id") ? searchParams.get("id") : null)
        .then((res) => {
          setPageLoading(false);
          //console.log("All packs ", res?.data);
          if (res?.events) {
            setMsisdn(res?.events?.msisdn);
            if (res?.participant) {
              setFlow("EXISTING");
              if (res.participant === 1) {
                dispatch(setUser(res?.events));
                window.location.href = "/home";
              } else if (res.participant === 2) {
                setFlowMsg({ number: "lowbalance", error: true }); //low balance;
              } else if (res.participant === 0) {
                setFlowMsg({ number: "inprocess", error: true }); //Charging in process;
              } else {
                dispatch(setUser(res?.events));
                window.location.href = "/home";
              }
            } else {
              dispatch(setUser(res?.events));
              window.location.href = "/home";
            }
          }
        })
        .catch((err) => {
          setPageLoading(false);
          console.log(err);
        });
    }
  }, []);

  const checkSubStatus = async (input) => {
    console.log("input=" + input);
    if (msisdn.length >= 8) {
      setLoading(true);
      await getSubStatusApi({ mobileNumber: parseInt(msisdn) })
        .then((res) => {
          setFlow("EXISTING");
          if (res.responseCode === 200) {
            dispatch(setUser(res?.data));
            if (input === "1") {
              window.location.href = "/home";
            } else setFlowMsg({ number: "alreadySub", error: false }); //already sub and charged ;
          } else if (res.responseCode === 203)
            setFlowMsg({ number: "lowbalance", error: true }); //low balance;
          else if (res.responseCode === 202)
            setFlowMsg({ number: "inprocess", error: true }); //Charging in process;
          else setFlow("PACK"); // select pack
        })
        .catch((err) => {
          console.log(err);
          toast.error(t("errorMsg"));
        })
        .finally(() => setLoading(false));
    } else {
      toast.error(t("invalidNumber"));
    }
  };

  const handleSubscription = async () => {
    if (msisdn?.trim().length === 0) {
      return;
    }
    setLoading(true);
    await subApi({
      mobileNumber: msisdn,
      packId: selectedPlan,
      languageId: String(selectedLanguage ?? 2),
    })
      .then((res) => {
        setLoading(false);
        if (res?.responseCode === 202) {
          // already subscribed and charged
          setFlow("EXISTING");
          setFlowMsg({ number: "alreadySub", error: false }); //New subscription;
          //dispatch(setUserDetails(res?.data));
          //window.location.href = "/home";
        } else {
          if (res?.data && isValidUrl(res?.data)) {
            setFlow("REDIRECT");
            window.location.href = res.data;
          } else {
            setFlow("EXISTING");
            setFlowMsg({ number: "subSuccess", error: false }); //New subscription;
          }
        }
      })
      .catch((err) => {
        setLoading(false);
        console.log(err);
        toast.error(t("errorMsg"));
      });
  };

  const handleSubscribeClick = () => {
    try {
      navigate("/privacy-supplement", {
        state: {
          msisdn: msisdn,
          plan: selectedPlan,
        },
      });
    } catch (error) {
      console.log(error, "error---");
    }
  };

  return (
    <main className={classes.main}>
      <div className={classes.glowLayer} />

      <LinedSection />

      <motion.section
        className={classes.container}
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      >
        <div className={classes.bgGlowOne} />
        <div className={classes.bgGlowTwo} />

        <div className={classes.topLogoWrap}>
          <img
            src="/images/logo2.png"
            alt="UV Gaming"
            className={classes.logoImg}
          />
        </div>

        <div className={classes.headingWrap}>
          <h1 className={classes.title}>
            {t("34")}
            <span> {t("35")}</span>
          </h1>
          {/* <p className={classes.subtitle}>
            Passez au niveau supérieur avec une nouvelle expérience de jeu
          </p> */}
        </div>

        <div className={classes.card}>
          <div className={classes.tabSwitch}>
            <button
              type="button"
              className={`${classes.tabBtn}`}
              onClick={() => navigate("/login")}
            >
              {t("36")}
            </button>

            <button
              type="button"
              className={`${classes.tabBtn} ${classes.tabBtnActive}`}
            >
              {t("37")}
            </button>
          </div>

          {flow === "EXISTING" ? (
            <>
              <h3
                className={
                  classes.loginTitle +
                  " " +
                  (flowMsg.error
                    ? classes.loginTitleRed
                    : classes.loginTitleGreen)
                }
              >
                {t(flowMsg.number)}
              </h3>

              <button
                disabled={loading}
                onClick={() => checkSubStatus("1")}
                className={classes.confirmBtn}
              >
                {loading ? (
                  <SmallLoader style={{ border: "5px solid #fff" }} />
                ) : (
                  t("clickHere")
                )}
              </button>
            </>
          ) : flow === "REDIRECT" ? (
            <>
              <h3 className={classes.loginTitleGreen}>{t("redirect")}</h3>
            </>
          ) : (
            <>
              <div className={classes.formBlock}>
                <label className={classes.label}>{t("38")}</label>

                <div className={classes.phoneField}>
                  <div className={classes.phoneCode}>+{countryCode}</div>
                  <input
                    type="tel"
                    placeholder={t("39")}
                    onChange={(e) => {
                      setMsisdn(
                        e.target.value && e.target.value.trim().length <= 10
                          ? e.target.value.trim()
                          : e.target.value.trim().slice(0, 10),
                      );
                    }}
                    value={
                      msisdn && msisdn.trim().length > 10
                        ? msisdn.trim().slice(3)
                        : msisdn
                    }
                    className={classes.phoneInput}
                  />
                </div>
              </div>

              <div className={classes.plansWrap}>
                <button
                  type="button"
                  className={`${classes.planCard} ${
                    selectedPlan === 1 ? classes.planCardActive : ""
                  }`}
                  onClick={() => setSelectedPlan(1)}
                >
                  <div className={classes.planInfo}>
                    <div
                      className={`${classes.planName} ${
                        selectedPlan === 1 ? classes.planNameActive : ""
                      }`}
                    >
                      {t("40")}
                    </div>

                    <div
                      className={`${classes.planDesc} ${
                        selectedPlan === 1 ? classes.planDescActive : ""
                      }`}
                    >
                      {t("41")}
                    </div>

                    <div
                      className={`${classes.planPrice} ${
                        selectedPlan === 1 ? classes.planPriceActive : ""
                      }`}
                    >
                      3 MT
                    </div>
                  </div>

                  <div
                    className={`${classes.selector} ${
                      selectedPlan === 1 ? classes.selectorActive : ""
                    }`}
                  >
                    {selectedPlan === 1 && <FaCheck />}
                  </div>
                </button>

                <button
                  type="button"
                  className={`${classes.planCard} ${
                    selectedPlan === 2 ? classes.planCardActive : ""
                  }`}
                  onClick={() => setSelectedPlan(2)}
                >
                  <div className={classes.planInfo}>
                    <div
                      className={`${classes.planName} ${
                        selectedPlan === 2 ? classes.planNameActive : ""
                      }`}
                    >
                      {t("42")}
                    </div>

                    <div
                      className={`${classes.planDesc} ${
                        selectedPlan === 2 ? classes.planDescActive : ""
                      }`}
                    >
                      {t("43")}
                    </div>

                    <div
                      className={`${classes.planPrice} ${
                        selectedPlan === 2 ? classes.planPriceActive : ""
                      }`}
                    >
                      20 MT
                    </div>
                  </div>

                  <div
                    className={`${classes.selector} ${
                      selectedPlan === 2 ? classes.selectorActive : ""
                    }`}
                  >
                    {selectedPlan === 2 && <FaCheck />}
                  </div>
                </button>

                <button
                  type="button"
                  className={`${classes.planCard} ${
                    selectedPlan === 3 ? classes.planCardActive : ""
                  }`}
                  onClick={() => setSelectedPlan(3)}
                >
                  <div className={classes.planInfo}>
                    <div
                      className={`${classes.planName} ${
                        selectedPlan === 3 ? classes.planNameActive : ""
                      }`}
                    >
                      {t("44")}
                    </div>

                    <div
                      className={`${classes.planDesc} ${
                        selectedPlan === 3 ? classes.planDescActive : ""
                      }`}
                    >
                      {t("45")}
                    </div>

                    <div
                      className={`${classes.planPrice} ${
                        selectedPlan === 3 ? classes.planPriceActive : ""
                      }`}
                    >
                      50 MT
                    </div>
                  </div>

                  <div
                    className={`${classes.selector} ${
                      selectedPlan === 3 ? classes.selectorActive : ""
                    }`}
                  >
                    {selectedPlan === 3 && <FaCheck />}
                  </div>
                </button>
              </div>

              <div className={classes.check_box_container}>
                <br />
                 {/*<input
                  type="checkbox"
                  id="terms"
                  name="Terms & Conditions"
                  value="Terms"
                  className={classes.check_box}
                  checked
                  readOnly
                />
               <label className={classes.check_box_label}>
                  <a href="/privacy-supplement">
                    <span className={classes.terms_conditions}>
                      {t("privacy")}
                    </span>
                  </a>
                </label> */}
              </div>

              <button
                type="button"
                className={classes.confirmBtn}
                disabled={loading}
                onClick={handleSubscription}
              >
                {loading ? (
                  <SmallLoader style={{ border: "5px solid #fff" }} />
                ) : (
                  t("2")
                )}
              </button>

              <div className={classes.bottomNote}>{t("47")}</div>
            </>
          )}
        </div>

        <Footer />
      </motion.section>
    </main>
  );
};

export default SubscriptionPage;
